import { InMemoryCache, NormalizedCacheObject } from 'apollo-cache-inmemory';
import { CachePersistor } from 'apollo-cache-persist';
import { PersistedData, PersistentStorage } from 'apollo-cache-persist/types';
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { withClientState } from 'apollo-link-state';
import * as objectHash from 'object-hash';
import {
  createRequestDecoratorLink,
  decorateApolloRequest,
  getToken
} from './shared/auth/teamsAuth';
import { getApiServerBaseUrl } from './shared/ceh-api/get-api-server-url';
import { cehGraphQLSchema } from './shared/data/ceh-graphql-schema';
import { clientResolvers } from './shared/data/client-resolvers';
import { clientStateSchema } from './shared/data/client-schema';
import { defaultClientState } from './shared/data/default-client-state';
import { isDev } from './shared/environments/is-environment';
import { createRetryErrorLink } from './shared/utils/ApolloLinks/ApolloRetryErrorLink';
import { graphQLErrorHandler } from './shared/utils/ApolloLinks/ApolloSurfaceGraphQLErrorLink';
import { createTelemetryErrorLink } from './shared/utils/ApolloLinks/ApolloTelemetryErrorLink';
import { createTelemetryLink } from './shared/utils/ApolloLinks/ApolloTelemetryLink';

const SCHEMA_VERSION_KEY = 'ceh-schema-version';

const apolloCache = new InMemoryCache({
  cacheRedirects: {
    Query: {
      userProfilePictureById: (_, args, { getCacheKey }) =>
        getCacheKey({ __typename: 'ProfilePicture', id: args.id }),
      ticket: (_, args, { getCacheKey }) =>
        getCacheKey({ __typename: 'Ticket', id: args.ticketId })
    }
  }
});

let networkLink: ApolloLink;

let dependenciesPromise: Promise<void>;

export let apolloClient: ApolloClient<NormalizedCacheObject> | undefined;
export let persistor: CachePersistor<NormalizedCacheObject> | undefined;

export const getTokenPromise = (): Promise<void> => {
  console.log('[apolloClient.ts] Initialize auth');
  return getToken().then(
    () => {
      console.log('[apolloClient.ts] auth loaded');
    },
    err => {
      console.error(
        '[apolloClient.ts] failed to get auth token',
        err && { name: err.name, message: err.message }
      );
      throw err;
    }
  );
};

console.log(
  'REACT_APP_DEV_ENABLE_MOCKS',
  process.env.REACT_APP_DEV_ENABLE_MOCKS
);
export const getApolloClient = (): Promise<void> => {
  if (process.env.REACT_APP_DEV_ENABLE_MOCKS === 'true') {
    // We are going to mock out our GraphQL calls
    dependenciesPromise = new Promise<void>(resolve => {
      import('./enable.mock').then(enableMocksModule => {
        networkLink = enableMocksModule.getMockApolloClient();
        resolve();
      });
    });
  } else {
    // We will make real GraphQL calls
    dependenciesPromise = new Promise<void>(resolve => {
      networkLink = createHttpLink({
        uri: getApiServerBaseUrl().toString(),
        credentials: 'same-origin'
      });
      resolve();
    });
  }
  return dependenciesPromise;
};

const decoratorLink = createRequestDecoratorLink(decorateApolloRequest);

const telemetryLink = createTelemetryLink();

const telemetryErrorLink = createTelemetryErrorLink();

const clientStateLink = withClientState({
  defaults: defaultClientState,
  resolvers: clientResolvers,
  typeDefs: clientStateSchema,
  cache: apolloCache
});

const retryErrorLink = createRetryErrorLink();
const surfaceGraphQLErrorLink = new ApolloLink(graphQLErrorHandler);

export const clientPromise = (channelId?: string) =>
  getApolloClient()
    .then(() => {
      apolloClient = new ApolloClient({
        link: ApolloLink.from([
          clientStateLink,
          decoratorLink,
          retryErrorLink, // retries request in case of errors before giving up
          surfaceGraphQLErrorLink, // catches GraphQL error and promotes it to network error
          telemetryLink, // logs latency and data error to telemetry
          telemetryErrorLink, // logs network error to telemetry
          networkLink
        ]),
        cache: apolloCache,
        connectToDevTools: isDev(),
        defaultOptions: {
          query: {
            fetchPolicy: 'network-only'
          },
          watchQuery: {
            fetchPolicy: 'cache-and-network'
          }
        }
      });
      return channelId;
    })
    .then(() => {
      persistor = new CachePersistor({
        cache: apolloCache,
        storage: window.localStorage as PersistentStorage<
          PersistedData<NormalizedCacheObject> | PersistedData<{}>
        >,
        key: channelId
      });
      const versionKey = `${SCHEMA_VERSION_KEY}-${channelId}`;
      const currentSchemaCache: string = objectHash.sha1(cehGraphQLSchema);
      const versionInCache: string | null = localStorage.getItem(versionKey);

      if (currentSchemaCache === versionInCache) {
        return new Promise<typeof persistor>(resolve => {
          // Immediately resolve this promise because the schemas match
          resolve();
        });
      } else {
        return new Promise<typeof persistor>(async resolve => {
          // Schema has changed, purge cache first then resolve
          console.log(
            '[apolloClient.ts] Purging cache from localStorage, due to schema change.'
          );
          await persistor?.purge();

          // Update the schema version in local storage
          localStorage.setItem(versionKey, currentSchemaCache);
          resolve();
        });
      }
    })
    .then(() => persistor?.restore());
